import { fmtPrice, t, market as resolveMarket } from "@/lib/terms";

// Shared "For Sale / Sold STC / Let" badge logic for public property cards.
// Mirrors the status filter widened in crm/public_routes.py — sold/let
// listings stay visible on the public site (never on the portal-feed
// export) so the site shows real, credible market activity.
//
// `market` ("uk" | "us", default "uk") localises the label — e.g. a US
// tenant shows "Under contract" / "Lease pending" / "Leased" instead of
// "Sold STC" / "Let Agreed" / "Let".
export function statusBadge(status, listingType, market = "uk") {
  switch (status) {
    case "under_offer":
      return {
        label: listingType === "letting" ? t("let_agreed", market) : t("sold_stc", market),
        tone: "warn",
      };
    case "sold":
      return { label: t("sold", market), tone: "muted" };
    case "let":
      return { label: t("let_done", market), tone: "muted" };
    default:
      return null;
  }
}

// THE price-qualifier list for the whole web app — the CRM's own dropdown
// (CrmProperties.jsx) imports it from here rather than keeping a second copy.
// The values are Foundation's canonical enumeration, PriceQualifier in
// backend/crm/models.py, which maps 1:1 onto Rightmove's own numeric
// PRICE_QUALIFIER codes (_PRICE_QUALIFIER_MAP in backend/rightmove.py); the
// mobile picker (PRICE_QUALIFIERS in mobile/src/lib/brand.js) carries the
// same twelve entries in the same order. All three have to stay identical —
// a value the feed doesn't know goes out as 0 and the listing goes live
// showing a plain price. `label` is the fuller wording the dropdown shows,
// `short` the inline form that sits in front of a price, so what a buyer sees
// here matches what they'd see on the agency's Rightmove listing. UK-only —
// US listings (MLS) don't carry these qualifiers.
export const PRICE_QUALIFIERS = [
  { value: "", label: "None, just the price", short: "" },
  { value: "guide_price", label: "Guide price", short: "Guide" },
  { value: "fixed_price", label: "Fixed price", short: "Fixed" },
  { value: "offers_over", label: "Offers over", short: "Offers over" },
  { value: "offers_in_excess_of", label: "Offers in excess of (OIEO)", short: "OIEO" },
  { value: "oiro", label: "Offers in the region of (OIRO)", short: "OIRO" },
  { value: "sale_by_tender", label: "Sale by tender", short: "Tender" },
  { value: "poa", label: "Price on application (POA)", short: "POA" },
  { value: "from", label: "From", short: "From" },
  { value: "shared_ownership", label: "Shared ownership", short: "Shared ownership" },
  { value: "part_buy_part_rent", label: "Part buy, part rent", short: "Part buy, part rent" },
  { value: "shared_equity", label: "Shared equity", short: "Shared equity" },
];

// Short form keyed by stored value, for display next to a price.
export const PRICE_QUALIFIER_SHORT = Object.fromEntries(
  PRICE_QUALIFIERS.filter((q) => q.value).map((q) => [q.value, q.short]),
);

// The property-type vocabulary, per market.
//
// The UK list is Zoopla's own RTDF property_type enum (see the note in
// CrmProperties.jsx: free text is accepted by the feed and then displayed
// under a generic type, which is how "Semi-Detached Cottage" got published).
// It is UK housing stock described in UK words, and almost none of it means
// anything to an American: there are no maisonettes, "terraced" is a
// townhouse, and a bungalow is a style rather than a category.
//
// The US list is the RESO Property Sub Type vocabulary every MLS keys from,
// so what an agent picks here is what their MLS expects. Both lists store
// the same field; only the options differ.
export const PROPERTY_TYPES_UK = [
  { value: "Detached", label: "Detached house" },
  { value: "Semi-Detached", label: "Semi-detached house" },
  { value: "Terraced", label: "Terraced house" },
  { value: "End Terrace", label: "End of terrace" },
  { value: "Town House", label: "Town house" },
  { value: "Link Detached", label: "Link-detached house" },
  { value: "Mews", label: "Mews" },
  { value: "Cottage", label: "Cottage" },
  { value: "Country House", label: "Country house" },
  { value: "Coach House", label: "Coach house" },
  { value: "Barn Conversion", label: "Barn conversion" },
  { value: "Farmhouse", label: "Farmhouse" },
  { value: "Bungalow", label: "Bungalow" },
  { value: "Detached Bungalow", label: "Detached bungalow" },
  { value: "Semi-Detached Bungalow", label: "Semi-detached bungalow" },
  { value: "Terraced Bungalow", label: "Terraced bungalow" },
  { value: "Flat", label: "Flat / apartment" },
  { value: "Maisonette", label: "Maisonette" },
  { value: "Studio", label: "Studio" },
  { value: "Block Of Flats", label: "Block of flats" },
  { value: "Park Home", label: "Park home" },
  { value: "Houseboat", label: "Houseboat" },
  { value: "Lodge", label: "Lodge" },
  { value: "Chalet", label: "Chalet" },
  { value: "Villa", label: "Villa" },
  { value: "Equestrian", label: "Equestrian" },
  { value: "Farm", label: "Farm" },
  { value: "Land", label: "Land" },
  { value: "Parking", label: "Parking" },
];

export const PROPERTY_TYPES_US = [
  { value: "SingleFamilyResidence", label: "Single family residence" },
  { value: "Condominium", label: "Condominium" },
  { value: "Townhouse", label: "Townhouse" },
  { value: "Duplex", label: "Duplex" },
  { value: "Triplex", label: "Triplex" },
  { value: "Quadruplex", label: "Fourplex" },
  { value: "MultiFamily", label: "Multi-family (5+ units)" },
  { value: "Apartment", label: "Apartment" },
  { value: "Stock Cooperative", label: "Co-op" },
  { value: "ManufacturedHome", label: "Manufactured home" },
  { value: "ManufacturedOnLand", label: "Manufactured home on land" },
  { value: "MobileHome", label: "Mobile home" },
  { value: "Cabin", label: "Cabin" },
  { value: "Farm", label: "Farm" },
  { value: "Ranch", label: "Ranch" },
  { value: "UnimprovedLand", label: "Land / lot" },
  { value: "Timeshare", label: "Timeshare" },
  { value: "OwnYourOwn", label: "Own-your-own" },
  { value: "Other", label: "Other" },
];

export function propertyTypes(market = "uk") {
  return resolveMarket(market) === "us" ? PROPERTY_TYPES_US : PROPERTY_TYPES_UK;
}

/**
 * The picker entry a stored value belongs to, or null if it is genuinely
 * something else.
 *
 * Matching on the exact string alone is what put "(unrecognised)" beside
 * perfectly ordinary listings. One live book carried Semi-Detached,
 * Semi-detached, Detached, Detached House, End Terrace and Flat — six
 * spellings of four types, because the field was free text for a year
 * before it was a list, and the CSV importer and the RTDF feed both still
 * write whatever the other system calls it. Every one of those maps onto
 * a picker entry without guessing at anything: case and separators are
 * spelling, and the trailing "House" on "Detached House" is the same word
 * the label already says.
 *
 * Deliberately no cleverer than that. "Period Cottage" is NOT folded into
 * "Cottage" and "Semi-Detached Cottage" is not folded into either: those
 * say something the picker does not, and quietly reclassifying a listing
 * is worse than admitting the value came from somewhere else. Those keep
 * their own value and the dialog keeps showing it.
 */
const typeKey = (v) => String(v || "")
  .toLowerCase()
  .replace(/[\s/_-]+/g, " ")
  .replace(/\s+(house|property)$/, "")
  .trim();

/**
 * The other names for a type the picker already offers.
 *
 * Taken from _PROPERTY_TYPE_ALIASES in backend/zoopla.py, so what the CRM
 * folds and what the feed folds are one rule rather than two that drift.
 * Only the entries whose answer IS one of our own values: Zoopla also
 * folds semi_detached_cottage and detached_cottage into "cottage", and
 * those are NOT mirrored here. Collapsing them is right for a feed, which
 * has an enum to satisfy; it is wrong for the record, which is allowed to
 * know the house is a semi-detached cottage. The feed can go on folding
 * what it has to fold.
 */
const TYPE_ALIASES = {
  "apartment": "Flat",
  "flat apartment": "Flat",
  "terrace": "Terraced",
  "mid terrace": "Terraced",
  "end of terrace": "End Terrace",
  "townhouse": "Town House",
  "semi": "Semi-Detached",
};

export function matchPropertyType(value, market = "uk") {
  const key = typeKey(value);
  if (!key) return null;
  const list = propertyTypes(market);
  const direct = list.find((t) => typeKey(t.value) === key);
  if (direct) return direct;
  // UK spellings only. The US list is RESO's vocabulary and shares none of
  // these words, so folding "apartment" there would be a guess.
  if (resolveMarket(market) === "us") return null;
  const aliased = TYPE_ALIASES[key];
  return aliased ? list.find((t) => t.value === aliased) || null : null;
}

/**
 * What to STORE for a typed or imported value: the picker's own spelling
 * when it is one of ours, otherwise the value untouched.
 *
 * The point is that two agents picking "Semi-detached house" from the same
 * dropdown produce the same string, so a book stays sortable and both
 * feeds receive one spelling rather than three.
 */
export function canonicalPropertyType(value, market = "uk") {
  const found = matchPropertyType(value, market);
  return found ? found.value : (value || "");
}

/**
 * How a home is owned. The UK stores "Freehold" or "Leasehold"; the US
 * equivalent distinction is fee simple against a condo or co-op interest,
 * and it is the HOA that a US buyer actually asks about.
 */
export const TENURES_UK = ["Freehold", "Leasehold", "Share of Freehold", "Commonhold"];
export const TENURES_US = ["Fee Simple", "Condominium", "Co-op", "Leasehold", "Land Lease"];

export function tenures(market = "uk") {
  return resolveMarket(market) === "us" ? TENURES_US : TENURES_UK;
}

/** The sensible default for a new record in each market. */
export function defaultTenure(market = "uk") {
  return resolveMarket(market) === "us" ? "Fee Simple" : "Freehold";
}

export function fmtListingPrice(p, market = "uk") {
  const raw = Number(p.listing_type === "letting" ? p.rental_pcm : p.asking_price) || 0;
  // Rightmove-style price qualifiers are a UK concept only.
  const qualifier = resolveMarket(market) === "us" ? null : PRICE_QUALIFIER_SHORT[p.price_qualifier];
  // Nothing priced yet (an appraisal not taken to instruction), or a genuine
  // POA listing — "£0" reads as free rather than "no figure on file".
  if (!raw) return qualifier === "POA" ? "POA" : "-";
  const amount = p.listing_type === "letting"
    ? fmtPrice(raw, market, { rent: true })
    : fmtPrice(raw, market);
  return qualifier ? `${qualifier} ${amount}` : amount;
}
